import React, { useState, useEffect, useMemo } from "react";
import { I18nextProvider } from "react-i18next";
import { FloatingButton } from "./FloatingButton";
import { ChatContainer } from "./ChatContainer";
import { GPCService } from "@/services/gpcService";
import i18n from "@/i18n";
import {
  WIDGET_POSITIONS,
  DEFAULT_WIDGET_CONFIG,
  BREAKPOINTS,
  API_URL,
  TURNSTILE_SITE_KEY,
} from "@/utils/constants";
import { isMobile } from "@/utils/helpers";
import "@/styles/index.css";
import "@/styles/widget.css";
import "@/styles/responsive.css";
export const TiaGPCWidget = ({
  token,
  turnstileSiteKey = TURNSTILE_SITE_KEY,
  theme = DEFAULT_WIDGET_CONFIG.theme,
  position = DEFAULT_WIDGET_CONFIG.position,
  language = DEFAULT_WIDGET_CONFIG.language,
  primaryColor,
  width = DEFAULT_WIDGET_CONFIG.width,
  height = DEFAULT_WIDGET_CONFIG.height,
  apiUrl = API_URL,
  onReady,
  onError,
  onClose,
  autoOpen = DEFAULT_WIDGET_CONFIG.autoOpen,
  showBranding = DEFAULT_WIDGET_CONFIG.showBranding,
  mobileFullscreen = DEFAULT_WIDGET_CONFIG.mobileFullscreen,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  const gpcService = useMemo(() => {
    return new GPCService(apiUrl, token);
  }, [apiUrl, token]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileDevice(isMobile());
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (language && i18n.changeLanguage) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    const loadConfig = async () => {
      if (!token) {
        setConfigLoading(false);
        return;
      }

      try {
        const response = await gpcService.getInitConfig();
        if (response.success) {
          setWidgetConfig(response.data);
          if (onReady) {
            onReady(response.data);
          }
        }
      } catch (error) {
        console.error("[TIA GPC Widget] Error loading config:", error);
        if (onError) {
          onError(error);
        }
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, [token, gpcService, onReady, onError]);

  if (!token) {
    console.error('[TIA GPC Widget] Error: "token" prop is required');
    return null;
  }

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const getPositionClass = () => {
    if (isMobileDevice && mobileFullscreen) {
      return "gpc-position-bottom-right";
    }
    return `gpc-position-${position}`;
  };

  const getCustomStyles = () => {
    const styles = {};

    if (primaryColor) {
      styles["--gpc-color-primary"] = primaryColor;
    }

    if (!isMobileDevice || !mobileFullscreen) {
      if (width) styles["--gpc-widget-width"] = width;
      if (height) styles["--gpc-widget-height"] = height;
    }

    return styles;
  };

  return (
    <I18nextProvider i18n={i18n}>
      <div
        className={`gpc-widget gpc-widget-container ${getPositionClass()} ${
          theme === "dark" ? "gpc-dark" : ""
        }`}
        style={getCustomStyles()}
      >
        {!isOpen && <FloatingButton onClick={() => setIsOpen(true)} />}

        {isOpen && !configLoading && (
          <ChatContainer
            gpcService={gpcService}
            licenseToken={token}
            widgetConfig={widgetConfig}
            turnstileSiteKey={turnstileSiteKey}
            onClose={handleClose}
            themeMode={theme}
            showBranding={showBranding}
            showMetadata={props.showMetadata || false}
            onError={onError}
          />
        )}
      </div>
    </I18nextProvider>
  );
};

TiaGPCWidget.defaultProps = {
  theme: "auto",
  position: "bottom-right",
  language: "en",
  width: "400px",
  height: "600px",
  autoOpen: false,
  showBranding: true,
  mobileFullscreen: true,
};